import { createContext, use, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { usePersistedEntitiesContext } from 'core/lib/PersistedEntities/PersistedEntitiesContext';
import { CSRD_LAST_SUBPAGE_ENTITY_KEY } from '#utils/lastSubpage';
import { createLatestPathSaveQueue } from '#utils/latestPathSaveQueue';

type CsrdSubpagePersistenceContextValue = {
  enqueueSave: (path: string, entityId?: string) => Promise<void>;
  getLatestPath: () => string | undefined;
};

const CsrdSubpagePersistenceContext = createContext<CsrdSubpagePersistenceContextValue | null>(
  null,
);

type ProviderProps = {
  children: ReactNode;
};

export const CsrdSubpagePersistenceProvider = ({ children }: ProviderProps) => {
  const { user } = useAuth0();

  return (
    <UserScopedCsrdSubpagePersistenceProvider key={user?.sub ?? 'unauthenticated'}>
      {children}
    </UserScopedCsrdSubpagePersistenceProvider>
  );
};

const UserScopedCsrdSubpagePersistenceProvider = ({ children }: ProviderProps) => {
  const { setEntity } = usePersistedEntitiesContext();
  const queueRef = useRef<ReturnType<typeof createLatestPathSaveQueue> | undefined>(undefined);

  queueRef.current ??= createLatestPathSaveQueue();
  const queue = queueRef.current;

  const enqueueSave = useCallback(
    (path: string, entityId?: string) =>
      queue.enqueue(
        path,
        (savedPath, id) => setEntity(CSRD_LAST_SUBPAGE_ENTITY_KEY, { path: savedPath }, id),
        entityId,
      ),
    [queue, setEntity],
  );
  const getLatestPath = useCallback(() => queue.getLatestPath(), [queue]);
  const value = useMemo(() => ({ enqueueSave, getLatestPath }), [enqueueSave, getLatestPath]);

  return <CsrdSubpagePersistenceContext value={value}>{children}</CsrdSubpagePersistenceContext>;
};

export const useCsrdSubpagePersistence = () => {
  const context = use(CsrdSubpagePersistenceContext);

  if (!context) {
    throw new Error('useCsrdSubpagePersistence must be used within its provider');
  }

  return context;
};
